'use client';

import { PropsWithChildren, createContext, useContext, useEffect, useState } from 'react';

export type ThemeProviderProps = {
  theme: 'light' | 'dark' | 'system';
  handleThemeChange: (theme: 'system' | 'light' | 'dark') => void;
};

export const ThemeContext = createContext<ThemeProviderProps>({
  theme: 'system',
  handleThemeChange(theme) {},
});

export const ThemeProvider = ({ children }: PropsWithChildren) => {
  const findInitialTheme = () => {
    if (typeof localStorage === 'undefined') return 'system'; // SSR (server-side rendering
    if (localStorage.getItem('pt-theme-preference') === 'light') return 'light';
    if (localStorage.getItem('pt-theme-preference') === 'dark') return 'dark';
    return 'system';
  };

  const [theme, setTheme] = useState<ThemeProviderProps['theme']>(findInitialTheme);

  const handleThemeChange = (theme: 'system' | 'light' | 'dark') => {
    localStorage.setItem('pt-theme-preference', theme);
    setTheme(theme);
  };

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  return <ThemeContext.Provider value={{ theme, handleThemeChange }}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => useContext(ThemeContext);
